import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Popover,
  Tooltip,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  HourglassEmptyOutlined,
  EditOutlined,
  CheckOutlined,
  CalendarTodayOutlined,
  ScienceOutlined,
  VisibilityOutlined,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { labResultService, lisDashboardService } from "../../services/lis";

// Stat card component for 3 top metrics in Results
function StatCard({ title, subtitle, value, icon, color, bgColor, isDark }) {
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const textTitleColor = isDark ? "#E2E8F0" : "#334155";
  const textSubtitleColor = isDark ? "#94A3B8" : "#94A3B8";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        borderRadius: "10px",
        bgcolor: cardBg,
        border: `1px solid ${borderColor}`,
        boxShadow: isDark
          ? "0 2px 8px rgba(0,0,0,0.25)"
          : "0 2px 8px rgba(15, 23, 42, 0.03)",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "8px",
          bgcolor: isDark ? `${color}25` : bgColor,
          color: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 20 } })}
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "0.78rem",
            fontWeight: 600,
            color: textTitleColor,
            lineHeight: 1.15,
            mb: 0.1,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "1.35rem",
            fontWeight: 800,
            color: color,
            lineHeight: 1.1,
            mb: 0,
          }}
        >
          {value ?? "—"}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Satoshi', sans-serif",
            fontSize: "0.68rem",
            color: textSubtitleColor,
            fontWeight: 400,
            lineHeight: 1,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
    </Paper>
  );
}

// Status badge pill for Results
function StatusBadge({ status, isDark }) {
  const statusStyles = {
    Pending: {
      color: isDark ? "#FBBF24" : "#D97706",
      bg: isDark ? "rgba(217, 119, 6, 0.15)" : "#FEF3C7",
      border: isDark ? "rgba(217, 119, 6, 0.3)" : "#FDE68A",
    },
    "In Progress": {
      color: "#2563EB",
      bg: isDark ? "rgba(37, 99, 235, 0.15)" : "#EFF6FF",
      border: isDark ? "rgba(37, 99, 235, 0.3)" : "#DBEAFE",
    },
    Completed: {
      color: "#16A34A",
      bg: isDark ? "rgba(22, 163, 74, 0.15)" : "#F0FDF4",
      border: isDark ? "rgba(22, 163, 74, 0.3)" : "#DCFCE7",
    },
    Amended: {
      color: "#EA580C",
      bg: isDark ? "rgba(234, 88, 12, 0.15)" : "#FFF7ED",
      border: isDark ? "rgba(234, 88, 12, 0.3)" : "#FFEDD5",
    },
  };

  const style = statusStyles[status] || {
    color: "#64748B",
    bg: isDark ? "rgba(100, 116, 139, 0.15)" : "#F1F5F9",
    border: isDark ? "rgba(100, 116, 139, 0.3)" : "#E2E8F0",
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 0.8,
        py: 0.1,
        height: 18,
        borderRadius: "4px",
        bgcolor: style.bg,
        border: `1px solid ${style.border}`,
        color: style.color,
        fontFamily: "'Satoshi', sans-serif",
        fontSize: "0.64rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </Box>
  );
}

// Date range helper for dynamic current month
const getDynamicDateRanges = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  const fmt = (d) =>
    `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
  return [
    { label: "Today", value: "Today" },
    { label: "Last 7 Days", value: "Last 7 Days" },
    { label: `This Month (${fmt(start)} - ${fmt(end)})`, value: "This Month" },
    { label: `This Year (${year})`, value: "This Year" },
    { label: `Year ${year - 1}`, value: `Year ${year - 1}` },
    { label: `Year ${year - 2}`, value: `Year ${year - 2}` },
    { label: "All Time", value: "All Time" },
  ];
};

export default function ResultsList({ isDark, onEnterResult, onViewReport }) {
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const textPrimary = isDark ? "#F8FAFC" : "#0F172A";
  const textSecondary = isDark ? "#94A3B8" : "#64748B";

  const [stats, setStats] = useState({
    resultPending: 0,
    inProgress: 0,
    completedResults: 0,
  });

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [statusTabIndex, setStatusTabIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Date range picker state
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [dateRangeText, setDateRangeText] = useState("All Time");

  // Fetch real data from backend
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        lisDashboardService.getResultsStats().catch(() => null),
        labResultService.list({ page: 1, limit: 200 }).catch(() => null),
      ]);

      if (statsRes?.data?.data) {
        setStats({
          resultPending: statsRes.data.data.drafts ?? 0,
          inProgress: statsRes.data.data.inProgress ?? 0,
          completedResults: statsRes.data.data.completed ?? 0,
        });
      }

      if (listRes?.data?.data && listRes.data.data.length > 0) {
        const mapped = listRes.data.data.map((item, idx) => ({
          id: item._id || `res-${idx}`,
          date: item.createdAt
            ? new Date(item.createdAt).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : new Date().toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              }),
          sampleId: item.specimen?.sampleId || item.specimenId?.sampleBarcode || item.sampleId || `SMP-${100 + idx}`,
          orderNumber: item.labOrder?.orderId || item.orderId?.orderNumber || item.orderNumber || `ORD-${new Date().getFullYear()}-${100 + idx}`,
          patient: {
            name:
              (item.patient?.firstName
                ? `${item.patient.firstName} ${item.patient.lastName || ""}`
                : item.orderId?.patientName || "Patient"),
            patientId:
              item.patient?.mrn ||
              item.patient?.patientId ||
              item.orderId?.patientMrn ||
              `PT-250512-00${idx + 1}`,
          },
          testName: item.test?.displayName || item.testCatalogId?.displayName || item.testName || "Full Blood Count (FBC)",
          status:
            item.status === "COMPLETED" || item.status === "completed" || item.status === "approved" || item.status === "verified" || item.status === "authorized"
              ? "Completed"
              : item.status === "IN_PROGRESS" || item.status === "in_progress" || item.status === "pending_review"
              ? "In Progress"
              : "Pending",
          raw: item,
        }));
        setResults(mapped);
      } else {
        setResults([]);
      }
    } catch (err) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtered results
  const filteredResults = useMemo(() => {
    return results.filter((item) => {
      const q = searchQuery.toLowerCase().trim();
      const itemDateStr = item.raw?.createdAt || item.raw?.enteredAt || item.raw?.updatedAt;
      const isYearQuery = /^\d{4}$/.test(q);
      const matchesYear = isYearQuery && itemDateStr && new Date(itemDateStr).getFullYear() === parseInt(q, 10);

      const matchesSearch =
        !q ||
        matchesYear ||
        item.sampleId?.toLowerCase().includes(q) ||
        item.orderNumber?.toLowerCase().includes(q) ||
        item.patient?.name?.toLowerCase().includes(q) ||
        item.patient?.patientId?.toLowerCase().includes(q) ||
        item.testName?.toLowerCase().includes(q);

      const matchesStatus =
        statusFilter === "All Status" ||
        item.status.toLowerCase() === statusFilter.toLowerCase();

      // Date Range Filter
      let matchesDate = true;
      if (dateRangeText && dateRangeText !== "All Time") {
        if (itemDateStr) {
          const d = new Date(itemDateStr);
          const now = new Date();
          if (dateRangeText === "Today") {
            matchesDate = d.toDateString() === now.toDateString();
          } else if (dateRangeText === "Last 7 Days") {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(now.getDate() - 7);
            sevenDaysAgo.setHours(0, 0, 0, 0);
            matchesDate = d >= sevenDaysAgo;
          } else if (dateRangeText === "This Month") {
            matchesDate =
              d.getMonth() === now.getMonth() &&
              d.getFullYear() === now.getFullYear();
          } else if (dateRangeText.startsWith("This Year")) {
            matchesDate = d.getFullYear() === now.getFullYear();
          } else if (dateRangeText.startsWith("Year ")) {
            const yr = parseInt(dateRangeText.replace("Year ", ""), 10);
            matchesDate = d.getFullYear() === yr;
          }
        }
      }

      return matchesSearch && matchesStatus && matchesDate;
    });
  }, [results, searchQuery, statusFilter, dateRangeText]);

  const totalPages = Math.ceil(filteredResults.length / pageSize) || 1;
  const paginatedResults = filteredResults.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // Handle action click
  const handleActionClick = (item) => {
    if (item.status === "Completed") {
      onViewReport?.(item.raw?._id || item.id);
    } else {
      onEnterResult?.(
        item.raw?.labOrderItem?._id || item.raw?.labOrderItem || item.raw?.orderItemId?._id || item.raw?.orderItemId,
        item.raw?.specimen?._id || item.raw?.specimen || item.raw?.specimenId?._id || item.raw?.specimenId,
        item.raw?.labOrder?._id || item.raw?.labOrder || item.raw?.orderId?._id || item.raw?.orderId
      );
    }
  };
  // Dynamic tab counts derived directly from results array
  const statusCounts = useMemo(() => {
    let pending = 0;
    let inProgress = 0;
    let completed = 0;
    results.forEach((r) => {
      if (r.status === "Pending") pending++;
      else if (r.status === "In Progress") inProgress++;
      else if (r.status === "Completed") completed++;
    });
    return {
      pending: results.length > 0 ? pending : stats.resultPending || 0,
      inProgress: results.length > 0 ? inProgress : stats.inProgress || 0,
      completed: results.length > 0 ? completed : stats.completedResults || 0,
    };
  }, [results, stats]);

  const STATUS_TABS = [
    { label: "All", filter: "All Status", count: results.length },
    { label: "Result Pending", filter: "Pending", count: statusCounts.pending, color: "#D97706" },
    { label: "In Progress", filter: "In Progress", count: statusCounts.inProgress, color: "#2563EB" },
    { label: "Completed", filter: "Completed", count: statusCounts.completed, color: "#16A34A" },
  ];

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      {/* Header Bar matching RIS Worklist */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
        <Box>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "1rem",
              fontWeight: 800,
              color: textPrimary,
              lineHeight: 1.2,
            }}
          >
            Results Queue
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "0.7rem",
              color: textSecondary,
            }}
          >
            Enter, review and manage laboratory test results — {filteredResults.length} results
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            size="small"
            onClick={fetchData}
            disabled={loading}
            sx={{ width: 26, height: 26, color: textSecondary }}
          >
            {loading ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Status Tabs with Badges */}
      <Tabs
        value={statusTabIndex}
        onChange={(_, v) => {
          setStatusTabIndex(v);
          setStatusFilter(STATUS_TABS[v].filter);
          setPage(1);
        }}
        sx={{
          mb: 0.5,
          borderBottom: 1,
          borderColor,
          minHeight: 28,
          "& .MuiTabs-indicator": {
            height: "2px",
            bgcolor: STATUS_TABS[statusTabIndex]?.color || "#2563EB",
          },
        }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {STATUS_TABS.map((t, idx) => (
          <Tab
            key={t.label}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <span>{t.label}</span>
                {t.count > 0 && (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      lineHeight: 1,
                      minWidth: 16,
                      height: 16,
                      px: 0.75,
                      borderRadius: 9999,
                      ml: 0.25,
                      flexShrink: 0,
                      bgcolor: t.color || (isDark ? "rgba(255,255,255,0.18)" : "#E2E8F0"),
                      color: t.color ? "#FFFFFF" : textPrimary,
                    }}
                  >
                    {t.count}
                  </Box>
                )}
              </Box>
            }
            sx={{
              textTransform: "none",
              minWidth: "auto",
              px: 1.2,
              py: 0.25,
              minHeight: 28,
              fontSize: "0.75rem",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 600,
              color: textSecondary,
              "&.Mui-selected": {
                color: t.color || (isDark ? "#FFFFFF" : "#0F172A"),
              },
            }}
          />
        ))}
      </Tabs>

      {/* Filter Bar (Search & Date Range) */}
      <Paper
        elevation={0}
        sx={{
          p: "4px 8px",
          mb: 0.75,
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexWrap: "wrap",
          borderRadius: "6px",
          border: `1px solid ${borderColor}`,
          bgcolor: cardBg,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by patient, sample ID, order ID, or test..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: textSecondary, fontSize: 14 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 220,
            flexGrow: 1,
            "& .MuiOutlinedInput-root": {
              height: 26,
              fontSize: "0.76rem",
              fontFamily: "'Satoshi', sans-serif",
              bgcolor: isDark ? "#141414" : "#FFFFFF",
              borderRadius: "4px",
              "& fieldset": { borderColor },
              "&:hover fieldset": { borderColor: "#3B82F6" },
            },
            "& .MuiOutlinedInput-input": { py: 0.25, px: 0.5 },
          }}
        />

        <Button
          size="small"
          onClick={(e) => setDateAnchorEl(e.currentTarget)}
          startIcon={<CalendarTodayOutlined sx={{ fontSize: 13, color: textSecondary }} />}
          sx={{
            borderRadius: "4px",
            border: `1px solid ${borderColor}`,
            textTransform: "none",
            color: textPrimary,
            px: 1,
            height: 26,
            fontSize: "0.74rem",
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 500,
            bgcolor: isDark ? "#141414" : "#FFFFFF",
            "&:hover": {
              borderColor: "#3B82F6",
            },
          }}
        >
          {dateRangeText}
        </Button>

        <Popover
          open={Boolean(dateAnchorEl)}
          anchorEl={dateAnchorEl}
          onClose={() => setDateAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{
            sx: {
              p: 1,
              bgcolor: cardBg,
              borderRadius: "6px",
              border: `1px solid ${borderColor}`,
            },
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "0.76rem", color: textPrimary, mb: 0.5 }}>
            Select Range
          </Typography>
          {getDynamicDateRanges().map((opt) => (
            <MenuItem
              key={opt.value}
              onClick={() => {
                setDateRangeText(opt.value);
                setDateAnchorEl(null);
              }}
              sx={{
                fontSize: "0.74rem",
                borderRadius: "4px",
                py: 0.4,
                fontWeight: dateRangeText === opt.value ? 700 : 400,
                bgcolor:
                  dateRangeText === opt.value
                    ? isDark
                      ? "rgba(37,99,235,0.15)"
                      : "#EFF6FF"
                    : "transparent",
              }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Popover>

        {(searchQuery || dateRangeText !== "All Time" || statusTabIndex !== 0) && (
          <Button
            size="small"
            onClick={() => {
              setSearchQuery("");
              setDateRangeText("All Time");
              setStatusTabIndex(0);
              setStatusFilter("All Status");
              setPage(1);
            }}
            sx={{
              height: 26,
              fontSize: "0.72rem",
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              color: textSecondary,
              py: 0,
              px: 1,
            }}
          >
            Clear
          </Button>
        )}

        <Typography variant="caption" sx={{ fontSize: "0.7rem", color: textSecondary, ml: "auto", fontFamily: "'Satoshi', sans-serif" }}>
          {filteredResults.length} shown
        </Typography>
      </Paper>

      {/* Results List Table Card */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "6px",
          bgcolor: cardBg,
          border: `1px solid ${borderColor}`,
          overflow: "hidden",
        }}
      >
        {/* Table Content */}
        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? "#171717" : "#F8FAFC" }}>
                {["Date", "Sample ID", "Patient", "Test / Profile", "Status", "Action"].map(
                  (header) => (
                    <TableCell
                      key={header}
                      align={header === "Action" ? "right" : "left"}
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        color: textSecondary,
                        py: "3px !important",
                        px: "6px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      {header}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} sx={{ color: "#2563EB" }} />
                  </TableCell>
                </TableRow>
              ) : paginatedResults.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ py: 4, color: textSecondary, fontFamily: "'Satoshi', sans-serif", fontSize: "0.78rem" }}
                  >
                    No results found matching your filters.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedResults.map((result) => (
                  <TableRow
                    key={result.id}
                    hover
                    sx={{
                      height: "28px",
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": {
                        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#F8FAFC",
                      },
                    }}
                  >
                    {/* 1. Date */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontSize: "0.72rem",
                        color: textSecondary,
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {result.date}
                    </TableCell>

                    {/* 2. Sample ID (Clickable Blue Link) */}
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: 700,
                        fontSize: "0.74rem",
                        color: "#2563EB",
                        cursor: "pointer",
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                        "&:hover": { textDecoration: "underline" },
                      }}
                      onClick={() => handleActionClick(result)}
                    >
                      {result.sampleId}
                    </TableCell>

                    {/* 3. Patient (Single-Line) */}
                    <TableCell
                      sx={{
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.74rem",
                            color: textPrimary,
                            maxWidth: 150,
                          }}
                        >
                          {result.patient?.name || "Patient"}
                        </Typography>
                        {result.patient?.patientId && (
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: "'Satoshi', sans-serif",
                              fontSize: "0.64rem",
                              color: textSecondary,
                            }}
                          >
                            · {result.patient.patientId}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* 4. Test / Profile (Single-Line) */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontSize: "0.72rem",
                        color: textPrimary,
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Typography noWrap sx={{ fontSize: "0.72rem", color: textPrimary, maxWidth: 200 }}>
                        {result.testName || "—"}
                      </Typography>
                    </TableCell>

                    {/* 5. Status */}
                    <TableCell
                      sx={{
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <StatusBadge status={result.status} isDark={isDark} />
                    </TableCell>

                    {/* 6. Action Button (Compact height: 24px) */}
                    <TableCell
                      align="right"
                      sx={{
                        px: "6px !important",
                        py: "2px !important",
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {result.status === "Pending" && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleActionClick(result)}
                          startIcon={<ScienceOutlined sx={{ fontSize: 13 }} />}
                          sx={{
                            borderRadius: "4px",
                            borderColor: "#3B82F6",
                            color: "#2563EB",
                            textTransform: "none",
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            px: 0.8,
                            py: 0.1,
                            height: 24,
                            whiteSpace: "nowrap",
                            "&:hover": {
                              borderColor: "#1D4ED8",
                              bgcolor: isDark ? "rgba(37, 99, 235, 0.1)" : "#EFF6FF",
                            },
                          }}
                        >
                          Enter Result
                        </Button>
                      )}

                      {result.status === "In Progress" && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleActionClick(result)}
                          startIcon={<EditOutlined sx={{ fontSize: 13 }} />}
                          sx={{
                            borderRadius: "4px",
                            borderColor: "#A855F7",
                            color: "#9333EA",
                            textTransform: "none",
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            px: 0.8,
                            py: 0.1,
                            height: 24,
                            whiteSpace: "nowrap",
                            "&:hover": {
                              borderColor: "#7E22CE",
                              bgcolor: isDark ? "rgba(147, 51, 234, 0.1)" : "#FAF5FF",
                            },
                          }}
                        >
                          Continue
                        </Button>
                      )}

                      {result.status === "Completed" && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleActionClick(result)}
                          startIcon={<VisibilityOutlined sx={{ fontSize: 13 }} />}
                          sx={{
                            borderRadius: "4px",
                            borderColor: "#22C55E",
                            color: "#16A34A",
                            textTransform: "none",
                            fontFamily: "'Satoshi', sans-serif",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            px: 0.8,
                            py: 0.1,
                            height: 24,
                            whiteSpace: "nowrap",
                            "&:hover": {
                              borderColor: "#15803D",
                              bgcolor: isDark ? "rgba(22, 163, 74, 0.1)" : "#F0FDF4",
                            },
                          }}
                        >
                          View Report
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 5. Pagination Footer */}
        <Box
          sx={{
            px: 1.5,
            py: 0.6,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: `1px solid ${borderColor}`,
            bgcolor: isDark ? "#171717" : "#FFFFFF",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "0.72rem",
              color: textSecondary,
            }}
          >
            Showing {(page - 1) * pageSize + 1} -{" "}
            {Math.min(page * pageSize, filteredResults.length)} of {filteredResults.length} results
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "0.7rem", color: textSecondary, fontFamily: "'Satoshi', sans-serif" }}>
              Rows:
            </Typography>
            <Select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              size="small"
              sx={{
                height: 24,
                fontSize: "0.72rem",
                fontFamily: "'Satoshi', sans-serif",
                "& .MuiSelect-select": { py: 0, px: 0.75 },
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <MenuItem key={n} value={n} sx={{ fontSize: "0.74rem" }}>
                  {n}
                </MenuItem>
              ))}
            </Select>

            <IconButton
              size="small"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              sx={{ width: 24, height: 24 }}
            >
              <KeyboardArrowLeft sx={{ fontSize: 16 }} />
            </IconButton>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                fontFamily: "'Satoshi', sans-serif",
                color: textPrimary,
              }}
            >
              {page} / {totalPages}
            </Typography>
            <IconButton
              size="small"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              sx={{ width: 24, height: 24 }}
            >
              <KeyboardArrowRight sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
